import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useReducer,
} from 'react'
import PropTypes from 'prop-types'
import { getEventSeries, getAvailableEventsForSeries, updateEventSeries, removeEventFromSeries } from 'api/events'
import { initStateObject, objectReducer } from 'reducers/default'

const EditEventSeriesContext = createContext(null)

export const useEditEventSeriesContext = () => {
  const context = useContext(EditEventSeriesContext)
  if (!context) {
    throw new Error('useEditEventSeriesContext must be used within EditEventSeriesProvider')
  }
  return context
}

export default function EditEventSeriesProvider({ children, eventId, onClose }) {
  // states that are filled with backend responses
  const [eventSeriesState, dispatchEventSeries] = useReducer(objectReducer, initStateObject)
  const [availableEventsState, dispatchAvailableEvents] = useReducer(
    objectReducer,
    initStateObject,
  )

  // local state of the modal (we work with flat arrays of events)
  const [initialIncludedEvents, setInitialIncludedEvents] = useState([])
  const [includedEvents, setIncludedEvents] = useState([])
  const [similarEvents, setSimilarEvents] = useState([])
  // events from filter tab
  const [filterTabEvents, setFilterTabEvents] = useState([])
  const [selectedEvents, setSelectedEvents] = useState(new Set())
  const [isSelecting, setIsSelecting] = useState(false)
  const [loading, setLoading] = useState(false)

  const reloadSeriesData = useCallback(async () => {
    if (!eventId) return

    await Promise.all([
      getEventSeries(dispatchEventSeries, eventId),
      getAvailableEventsForSeries(dispatchAvailableEvents, eventId),
    ])
  }, [eventId])

  useEffect(() => {
    reloadSeriesData()
  }, [reloadSeriesData])

  useEffect(() => {
    const backendIncludedEvents = eventSeriesState.content?.series ?? []
    setInitialIncludedEvents(backendIncludedEvents)
    setIncludedEvents(backendIncludedEvents)
  }, [eventSeriesState.content])

  useEffect(() => {
    const backendAvailableEvents = availableEventsState.content?.items ?? []
    const sorted = [...backendAvailableEvents].sort(
      (a, b) => new Date(a.start_date_time) - new Date(b.start_date_time)
    )
    setSimilarEvents(sorted)
  }, [availableEventsState.content])

  // ids of events that backend exposes as "available for series"
  const availableEventIds = useMemo(() => {
    const backendAvailableEvents = availableEventsState.content?.items ?? []
    return new Set(backendAvailableEvents.map((e) => e.id))
  }, [availableEventsState.content])

  // get all events (included + similar + filter-tab)
  const allEvents = useMemo(() => {
    return [...includedEvents, ...similarEvents, ...filterTabEvents]
  }, [includedEvents, similarEvents, filterTabEvents])

  // track added and removed events
  const addedEvents = useMemo(() => {
    const initialIds = new Set(initialIncludedEvents.map((e) => e.id))
    return includedEvents.filter((event) => !initialIds.has(event.id))
  }, [includedEvents, initialIncludedEvents])

  const removedEvents = useMemo(() => {
    const currentIds = new Set(includedEvents.map((e) => e.id))
    return initialIncludedEvents.filter((event) => !currentIds.has(event.id))
  }, [includedEvents, initialIncludedEvents])

  // check if there are any changes
  const hasChanges = useMemo(() => {
    return addedEvents.length > 0 || removedEvents.length > 0
  }, [addedEvents.length, removedEvents.length])

  // check if event is included
  const isEventIncluded = useCallback(
    (eventId) => {
      return includedEvents.some((event) => event.id === eventId)
    },
    [includedEvents]
  )

  // get event by id
  const getEventById = useCallback(
    (eventId) => {
      return allEvents.find((event) => event.id === eventId)
    },
    [allEvents]
  )

  // remove event from included
  const removeEventFromIncluded = useCallback((eventId) => {
    const event = getEventById(eventId)
    if (!event || !isEventIncluded(eventId)) return

    setIncludedEvents((prev) => prev.filter((e) => e.id !== eventId))
    setSimilarEvents((prev) => {
      // if event didn't come from "available_events_for_series",
      // do not add it back to similarEvents when it is removed from the series
      if (!availableEventIds.has(eventId)) {
        return prev
      }
      const updated = [...prev, event]
      return updated.sort((a, b) => new Date(a.start_date_time) - new Date(b.start_date_time))
    })
    setSelectedEvents((prev) => {
      const newSet = new Set(prev)
      newSet.delete(eventId)
      return newSet
    })
  }, [getEventById, isEventIncluded, availableEventIds])

  // add multiple events
  const addEventsToIncluded = useCallback(
    (eventIds) => {
      const uniqueIds = Array.from(new Set(eventIds))
  
      setIncludedEvents((prevIncluded) => {
        const existingIds = new Set(prevIncluded.map((e) => e.id))
  
        const eventsToAdd = uniqueIds
          .map((id) => getEventById(id))
          .filter((event) => event && !existingIds.has(event.id))
  
        const updated = [...prevIncluded, ...eventsToAdd]
        return updated.sort(
          (a, b) => new Date(a.start_date_time) - new Date(b.start_date_time)
        )
      })
  
      setSimilarEvents((prevSimilar) => {
        const idsToAdd = new Set(uniqueIds)
        const updated = prevSimilar.filter((e) => !idsToAdd.has(e.id))
        return updated.sort(
          (a, b) => new Date(a.start_date_time) - new Date(b.start_date_time)
        )
      })
  
      setSelectedEvents((prevSelected) => {
        const next = new Set(prevSelected)
        uniqueIds.forEach((id) => next.delete(id))
        return next
      })
    },
    [getEventById]
  )

  // add one event to included
  const addEventToIncluded = useCallback((eventId) => {
    addEventsToIncluded([eventId])
  }, [addEventsToIncluded])
  

  // toggle event selection
  const toggleEventSelection = useCallback((eventId) => {
    setSelectedEvents((prev) => {
      // create a new Set from the array to guarantee update
      const prevArray = Array.from(prev)
      const newSet = new Set(prevArray)
      
      if (newSet.has(eventId)) {
        newSet.delete(eventId)
      } else {
        newSet.add(eventId)
      }
      
      // return the new Set to React to notice the change
      return new Set(newSet)
    })
  }, [])

  // select all events in the current list
  const selectAllEvents = useCallback((events) => {
    const eventIds = events.map((event) => event.id)
    setSelectedEvents(new Set(eventIds))
  }, [])

  // clear selection
  const clearSelection = useCallback(() => {
    setSelectedEvents(new Set())
    setIsSelecting(false)
  }, [])

  // add all selected events (or all events if nothing is selected)
  const addAllEvents = useCallback((events) => {
    if (selectedEvents.size > 0) {
      // add only selected events
      addEventsToIncluded(Array.from(selectedEvents))
    } else {
      // add all events from the list
      const eventIds = events.map((event) => event.id)
      addEventsToIncluded(eventIds)
    }
    clearSelection()
  }, [selectedEvents, addEventsToIncluded, clearSelection])

  const resetState = useCallback(() => {
    // restore similar/included events from the last backend response
    setIncludedEvents(initialIncludedEvents)
    const backendAvailableEvents = availableEventsState.content?.items ?? []
    const sorted = [...backendAvailableEvents].sort(
      (a, b) => new Date(a.start_date_time) - new Date(b.start_date_time)
    )
    setSimilarEvents(sorted)
    // clear selection
    setSelectedEvents(new Set())
    setIsSelecting(false)
  }, [initialIncludedEvents, availableEventsState.content])

  // save changes
  const saveChanges = useCallback(async () => {
    setLoading(true)
    try {
      const eventIdsToUpdate = includedEvents.map((e) => e.id)
      const addedEventIds = addedEvents.map((e) => e.id)
      const eventIdsToRemove = removedEvents.map((e) => e.id)

      if (addedEventIds.length > 0) {
        const resultUpdate = await updateEventSeries(eventId, eventIdsToUpdate)
        if (!resultUpdate?.success) {
          throw new Error('eventSeriesSaveFailed')
        }
      }
      
      if (eventIdsToRemove.length > 0) {
        const resultRemove = await removeEventFromSeries(eventId, eventIdsToRemove)
        if (!resultRemove?.success) {
          throw new Error('eventSeriesSaveFailed')
        }
      }
      
      onClose?.()
    } catch (error) {
      console.error('Error saving event series:', error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [eventId, includedEvents, onClose])

  // register events from filter tab
  const registerFilterTabEvents = useCallback((eventsFromFilter) => {
    setFilterTabEvents(eventsFromFilter ?? [])
  }, [])

  const value = useMemo(
    () => ({
      // State
      includedEvents,
      similarEvents,
      selectedEvents,
      isSelecting,
      loading,
      hasChanges,

      // Actions
      setIsSelecting,
      registerFilterTabEvents,
      addEventToIncluded,
      removeEventFromIncluded,
      toggleEventSelection,
      selectAllEvents,
      clearSelection,
      addAllEvents,
      saveChanges,
      isEventIncluded,
      resetState,
      reloadSeriesData,
    }),
    [
      includedEvents,
      similarEvents,
      selectedEvents,
      isSelecting,
      loading,
      hasChanges,
      registerFilterTabEvents,
      addEventToIncluded,
      removeEventFromIncluded,
      toggleEventSelection,
      selectAllEvents,
      clearSelection,
      addAllEvents,
      saveChanges,
      isEventIncluded,
      resetState,
      reloadSeriesData,
    ]
  )

  return (
    <EditEventSeriesContext.Provider value={value}>
      {children}
    </EditEventSeriesContext.Provider>
  )
}

EditEventSeriesProvider.propTypes = {
  children: PropTypes.node.isRequired,
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onClose: PropTypes.func,
}
